// The RingCentral sign-in pop-up (authorization code with PKCE; the server keeps the verifier and exchanges the code).
// Used by an agent signing in as themselves (client.ts) and by an admin signing a workstation in (Setup → Workstations).
// RingCentral sends the pop-up back to /rc-callback.html, which hands the code and state to this window (same origin
// only) and closes. Nothing is stored in the browser.

import { api } from '../api.ts';

const SIGN_IN_TIMEOUT_MS = 5 * 60_000;

/** Opens the pop-up at the URL `start` returns, waits for RingCentral to send the operator back, and completes it. */
export async function rcSignIn(start: () => Promise<{ authorizeUrl: string }>): Promise<{ signedIn: boolean; extensionId: string }> {
  const popup = window.open('about:blank', 'eyeq-rc-sign-in', 'width=480,height=720');
  if (!popup) throw new Error('allow pop-ups for this site to sign in to RingCentral');
  let url: string;
  try {
    url = (await start()).authorizeUrl;
  } catch (err) {
    popup.close();
    throw err;
  }
  popup.location.href = url;
  const { code, state } = await new Promise<{ code: string; state: string }>((resolve, reject) => {
    const done = (fn: () => void) => {
      window.removeEventListener('message', onMsg);
      clearInterval(watch);
      clearTimeout(timer);
      fn();
    };
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== popup) return;
      const d = e.data as { type?: string; code?: string; state?: string; error?: string } | null;
      if (d?.type !== 'eyeq-rc-callback') return;
      done(() => (d.code && d.state ? resolve({ code: d.code, state: d.state }) : reject(new Error(d.error ? `RingCentral: ${d.error}` : 'RingCentral sign-in was cancelled'))));
    };
    const watch = setInterval(() => {
      if (popup.closed) done(() => reject(new Error('RingCentral sign-in was closed before it finished')));
    }, 500);
    const timer = setTimeout(() => done(() => reject(new Error('RingCentral sign-in took too long'))), SIGN_IN_TIMEOUT_MS);
    window.addEventListener('message', onMsg);
  });
  return api.softphoneLoginComplete(code, state);
}
